import { useCallback, useEffect, useRef, useState, useMemo } from "react";
import { Application, Texture } from "pixi.js";
import { fromRgbHexValue } from "./palette";
import { Viewport } from "pixi-viewport";
import { MosaicContainer, tileWidth } from "./particle";
import { fitAnimate, fitToScreen, hintPan } from "./viewport";
import { Brush } from "./brush";
import { loadImageForPixi, processImage, type ProcessedImage } from "./image";
import manifest from "./assets/manifest.json";

import "./App.css";
import ColorProgress from "./ColorProgress";
import { initInProgress, saveMosaic, deleteDrawing } from "./db/save";
import { confetti } from "./confetti";
import throttle from "lodash/throttle";
import { BuiltinGallery, type BuiltinGalleryEntry } from "./BuiltinGallery";
import { SavedGallery, type SavedGalleryEntry } from "./SavedGallery";
import { Db, deserializeDrawing, serializeDrawing } from "./db/db";
import { toPng, type Drawing } from "./db/drawing";
import { StarRatingOverlay } from "./stars";
import { BlankSpace } from "./Blank";
import { PixelLoader } from "./loading";
import { SettingsSelect } from "./SettingsSelect";

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const defaultDetail = 3; // 2 - 4

function App() {
  const canvasRef = useRef<HTMLDivElement>(null);
  const appRef = useRef<Application | null>(null);
  const viewportRef = useRef<Viewport | null>(null);

  const particleContainerRef = useRef<MosaicContainer | null>(null);
  const brushRef = useRef<Brush | null>(null);

  // Controls
  const [detail, setDetail] = useState<number>(2);
  const [showDetailSelector, setShowDetailSelector] = useState(false);

  // State
  const [isInit, setIsInit] = useState(false); // whether a mosaic is loaded
  const [isLoading, setIsLoading] = useState(true); // loading image
  const [showEnd, setShowEnd] = useState(false);
  const [colouringFinished, setColouringFinished] = useState(false);
  const [loadedImage, setLoadedImage] = useState<ProcessedImage | null>(null);
  // palette colors and selected color
  const [palette, setPalette] = useState<Array<number>>([]);
  const [selectedColorIndex, setSelectedColorIndex] = useState<number>();
  const selectedColor: number | undefined =
    selectedColorIndex === undefined
      ? undefined
      : particleContainerRef.current?.getPaletteColorByIndex(
          selectedColorIndex
        );
  // add a ref for the scrollable palette
  const paletteScrollRef = useRef<HTMLDivElement | null>(null);
  const [isColouring, setIsColouring] = useState<boolean>(false);

  // File input for image capture on mobile
  const fileInputRef = useRef<HTMLInputElement>(null);
  const fileCameraInputRef = useRef<HTMLInputElement>(null);
  const fileFolderInputRef = useRef<HTMLInputElement>(null);

  // Force re-render
  const [, setChangelog] = useState(0);
  // debug flag from query param
  const isDebug =
    new URLSearchParams(window.location.search).get("debug") === "true";
  // copy current drawing as base64 to clipboard
  const copyCurrentDrawing = useCallback(() => {
    const pc = particleContainerRef.current;
    if (!pc) return;
    const drawing = pc.toDrawing();
    const buffer = serializeDrawing(drawing);
    const bytes = new Uint8Array(buffer);
    let binary = "";
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    const base64 = btoa(binary);
    navigator.clipboard.writeText(base64);
  }, []);

  const openCameraPicker = useCallback(() => {
    fileCameraInputRef.current?.click();
  }, []);
  const openFolderPicker = useCallback(() => {
    fileFolderInputRef.current?.click();
  }, []);

  const selectColour = (idx: number) => {
    setSelectedColorIndex(idx);
    particleContainerRef.current?.selectColour(idx);
  };

  const toParticleContainer = async (
    app: Application,
    pc: MosaicContainer,
    bitmap: ImageBitmap | HTMLImageElement,
    detail: number,
    id?: string
  ): Promise<ProcessedImage | undefined> => {
    const texture = Texture.from(bitmap);
    const img = await processImage(app, texture, detail);
    if (!img) {
      // ignore
      return;
    }

    setLoadedImage(img);
    await pc.load(img, id);

    return img;
  };

  const confirmDetail = useCallback(async () => {
    const app = appRef.current;
    if (!app) return;
    // hide the detail selector
    setShowDetailSelector(false);

    // pan to selected color
    const pc = particleContainerRef.current;
    const canvas = canvasRef.current;
    const vp = viewportRef.current;

    const panIdx = selectedColorIndex;

    pc?.setNormal();

    if (panIdx !== undefined && pc && canvas && vp) {
      hintPan(panIdx, vp, pc);
    }
  }, [selectedColorIndex]);

  const updateMosaic = useCallback(
    (canvas: HTMLElement, vp: Viewport, pc: MosaicContainer, pan = true) => {
      if (!pc.isLoaded) return;

      setIsInit(true);

      const rect = canvas.getBoundingClientRect();

      // Fit view to the particle grid
      fitToScreen(rect, vp, pc, true);

      // Update react palette bar
      const palette = pc.getPalette();
      setPalette(palette);

      // select the next color
      const nextIdx = nextSelectedColour(undefined, palette);
      if (nextIdx !== undefined) {
        selectColour(nextIdx);
        // and pan to it
        if (pan) {
          setTimeout(() => {
            setIsLoading(false);
            hintPan(nextIdx, vp, pc);
          }, 100);
        } else {
          setIsLoading(false);
        }
      } else {
        setIsLoading(false);
      }

      saveMosaic(pc);

      // reset changelog
      setChangelog(0);
      setShowEnd(pc.getTotalProgress() >= 1);
      setReplayReady(pc.getTotalProgress() >= 1);
      setColouringFinished(false); // not colouring - just loaded it complete

      // resume viewport dragging
      vp.plugins.resume("drag");
      vp.plugins.resume("pinch");
      vp.plugins.resume("wheel");
    },
    []
  );

  const setDetailValue = useCallback(
    (value: number) => {
      setDetail(value);

      const run = async () => {
        // Update particle container
        const pc = particleContainerRef.current;
        const app = appRef.current;
        const texture = loadedImage?.src;
        if (!pc || !app || !texture) {
          return;
        }

        if (pc.getTotalProgressClicks() > 0) {
          // don't update detail if they've started
          return;
        }

        pc.renderLoading(true);
        const img = await processImage(app, texture, value);
        function wait(ms: number) {
          return new Promise((resolve) => setTimeout(resolve, ms));
        }
        await wait(500);
        pc.renderLoading(false);
        if (!img) {
          // ignore
          return;
        }

        // update the particle container straight away
        const canvas = canvasRef.current;
        const vp = viewportRef.current;
        const bmp = loadedImage?.src;
        if (!bmp || !canvas || !vp) return;

        setLoadedImage(img);
        await pc.load(img);
        updateMosaic(canvas, vp, pc, false);
        pc.setPreview();
      };

      run().catch((err) => {
        console.error("Failed to update detail:", err);
      });
    },
    [loadedImage, updateMosaic]
  );

  const onFileChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const app = appRef.current;
      const pc = particleContainerRef.current;
      const canvas = canvasRef.current;
      const vp = viewportRef.current;
      if (!app || !pc || !canvas || !vp) return;

      const file = e.target.files?.[0];
      if (file && file.type.startsWith("image/")) {
        try {
          setIsLoading(true);
          const bmp = await loadImageForPixi(file);
          setIsColouring(false);
          setDetail(defaultDetail);
          // Build/update particles (this should set pc.gridW/H, pc.palette, pc.colorGrid)
          const img = await toParticleContainer(app, pc, bmp, defaultDetail);
          if (!img) {
            return;
          }
          updateMosaic(canvas, vp, pc, false);
          pc.setPreview();

          // for custom files, show the detail selector
          // showPreview(app, img);
          setShowDetailSelector(true);
        } catch (err) {
          console.error("Failed to load image:", err);
        }
      }
      e.target.value = "";
    },
    [updateMosaic]
  );

  // Gallery modal state and pagination
  const { images } = manifest as { images: BuiltinGalleryEntry[] };
  const [galleryList, setGalleryList] = useState<BuiltinGalleryEntry[]>(
    images.map((img) => ({
      ...img,
      src: `/${img.src}`,
      thumb: `/${img.thumb}`,
    }))
  );
  const [showGallery, setShowGallery] = useState(false);

  const openGallery = async () => {
    await refreshSaved();
    return setShowGallery(true);
  };
  const closeGallery = () => setShowGallery(false);
  const loadBuiltin = async (entry: BuiltinGalleryEntry) => {
    closeGallery();
    setIsLoading(true);

    // see if it was already saved
    const existing = savedWorks.find((s) => s.drawing.id === entry.id);
    if (existing) {
      // If it exists, load it
      loadDrawing(existing.drawing);
      return;
    }

    const pc = particleContainerRef.current;
    const app = appRef.current;
    const canvas = canvasRef.current;
    const vp = viewportRef.current;
    if (!pc || !app || !canvas || !vp) return;

    try {
      const url = entry.src;
      const resp = await fetch(url);
      const blob = await resp.blob();
      const file = new File([blob], entry.src, { type: blob.type });
      const bmp = await loadImageForPixi(file);
      setDetail(defaultDetail);
      // Build/update particles (this should set pc.gridW/H, pc.palette, pc.colorGrid)
      await toParticleContainer(app, pc, bmp, defaultDetail, entry.id);
      updateMosaic(canvas, vp, pc);
    } catch (err) {
      console.error("Failed to load gallery image:", err);
    }
  };

  const checkEnd = () => {
    const pc = particleContainerRef.current;
    if (!pc || !viewportRef.current) {
      return false;
    }

    const totalProg = pc.getTotalProgress();
    if (totalProg < 1) {
      return false;
    }
    fitAnimate(viewportRef.current, () => {
      // whole mosaic done!
      const intervals = [0, 500, 600, 1000, 1100, 1600];
      for (const t of intervals) {
        setTimeout(() => {
          confetti(pc.getPalette());
        }, t);
      }

      setShowEnd(true);
      setColouringFinished(true);

      // start animating a redraw of the whole picture
      setTimeout(() => {
        pc.redraw();
      }, 6000);
    });

    // disable viewport dragging & zooming
    viewportRef.current?.plugins.pause("drag");
    viewportRef.current?.plugins.pause("pinch");
    viewportRef.current?.plugins.pause("wheel");

    return true;
  };

  const loadDrawing = useCallback(
    async (drawing: Drawing) => {
      setShowSaved(false);

      // check if already loaded
      if (particleContainerRef.current?.drawingId === drawing.id) {
        setIsLoading(false);
        return;
      }

      setIsLoading(true);

      const pc = particleContainerRef.current;
      const app = appRef.current;
      const canvas = canvasRef.current;
      const vp = viewportRef.current;
      if (!pc || !app || !canvas || !vp) return;

      setTimeout(() => {
        try {
          pc.fromDrawing(drawing).then(() => {
            updateMosaic(canvas, vp, pc);
            setIsColouring(false);
          });
        } catch (err) {
          console.error("Failed to load drawing:", err);
        }
      }, 100);
    },
    [updateMosaic]
  );

  // import drawing from base64
  const importDrawing = useCallback(
    (drawingb64: string) => {
      const binary = atob(drawingb64);
      const len = binary.length;
      const bytes = new Uint8Array(len);
      for (let i = 0; i < len; i++) {
        bytes[i] = binary.charCodeAt(i);
      }
      const buffer = bytes.buffer;
      try {
        const drawing = deserializeDrawing(buffer);
        loadDrawing(drawing);
      } catch {
        console.error("Failed to import drawing");
      }
    },
    [loadDrawing]
  );

  const tryColouring = useCallback(
    (x: number, y: number): boolean => {
      // if no selected color, do nothing
      const pc = particleContainerRef.current;

      if (!selectedColor || !pc || selectedColorIndex === null) {
        return false;
      }

      if (pc.isShown(x, y)) {
        // if already shown, do nothing
        return false;
      }

      if (!pc.isClickable()) {
        return false; // no clicks if numbers aren't visible
      }

      // get the particle container colour
      const clickColourIndex = pc.getPaletteIndex(x, y);
      if (clickColourIndex !== selectedColorIndex) {
        // do nothing if clicked color is not the selected one
        return false;
      }

      pc.click(x, y); // show the particle at the clicked position
      return true;
    },
    [selectedColor, selectedColorIndex]
  );

  const onClickTile = useCallback(() => {
    const isStarted = brushRef.current?.applyHitbox(tryColouring);

    if (isStarted) {
      setIsColouring(true);
      viewportRef.current?.plugins.pause("drag");
    }
  }, [tryColouring]);

  const onMoveTile = useCallback(() => {
    // x: number, y: number) => {
    // if not colouring, do nothing
    if (!isColouring) return;

    if (brushRef.current?.applyHitbox(tryColouring)) {
      // force a re-render to update progress bars
      setChangelog((c) => c + 1);
    }
  }, [isColouring, tryColouring]);

  const getPaletteProgress = (idx: number): number => {
    return particleContainerRef.current?.getProgress(idx) ?? 0;
  };

  const getTotalProgress = () => {
    return particleContainerRef.current?.getTotalProgressClicks() ?? 0;
  };

  const nextSelectedColour = (
    selected: number | undefined,
    pals: Array<number>
  ): number | undefined => {
    const nPalettes = pals.length;
    const pc = particleContainerRef.current;
    if (!pc) return;

    const fromIndex = selected ?? -1;

    let nextIdx = -1;
    // Single pass: start after current, wrap around to current
    for (let offset = 1; offset < nPalettes; offset++) {
      const i = (fromIndex + offset) % nPalettes;
      if ((pc.getProgress(i) ?? 0) < 1) {
        nextIdx = i;
        break;
      }
    }
    return nextIdx >= 0 ? nextIdx : undefined;
  };

  // share handler: create a Blob URL and trigger a download
  const onShare = useCallback(async () => {
    const pc = particleContainerRef.current;
    if (!pc) return;

    // Get image blob
    const pngBlob: Blob = await pc.toPng(); // assuming this returns a Blob
    const file = new File([pngBlob], "colourn.png", { type: "image/png" });

    // 1) Best: native share sheet with file (Web Share Level 2)
    try {
      if (navigator.canShare?.({ files: [file] })) {
        await navigator.share({
          files: [file],
        });
        return;
      }
    } catch (err) {
      console.warn("navigator.share(files) failed:", err);
      // continue to fallbacks
    }

    // 3) Fallback: open native share sheet without file (older iOS/Android)
    try {
      if (navigator.share) {
        await navigator.share({
          title: "Check out my drawing",
          url: window.location.origin, // share your site/app link
        });
        return;
      }
    } catch (err) {
      console.warn("navigator.share(url) failed:", err);
      // continue to fallback
    }
  }, []);

  const onShareRecording = useCallback(async () => {
    const pc = particleContainerRef.current;
    if (!pc) return;
    const blob = pc.getLastRecordingBlob();
    if (!blob) {
      console.warn("No recording available");
      return onShare(); // fallback to image share
    }

    const filename = "drawing." + (blob.type === "video/webm" ? "webm" : "mp4");

    const file = new File([blob], filename, { type: blob.type });

    if (navigator.canShare && navigator.canShare({ files: [file] })) {
      try {
        await navigator.share({
          files: [file],
          title: "Check out my drawing",
        });
        return;
      } catch (err) {
        console.warn("Share cancelled or failed:", err);
      }
    }

    // --- Fallback: force a download ---
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a); // for Firefox
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }, [onShare]);

  const onRestart = useCallback(async () => {
    if (!window.confirm("Restart and lose current progress?")) {
      return;
    }

    if (
      !canvasRef.current ||
      !viewportRef.current ||
      !particleContainerRef.current
    ) {
      return;
    }

    setIsLoading(true);
    particleContainerRef.current?.restart();
    updateMosaic(
      canvasRef.current!,
      viewportRef.current,
      particleContainerRef.current
    );
  }, [updateMosaic]);

  // debounced save, fires at most once every 5s
  const saveDebounced = useMemo(
    () =>
      throttle(() => {
        const pc = particleContainerRef.current;
        if (pc) saveMosaic(pc);
      }, 5000),
    []
  );

  // Initialize Pixi
  useEffect(() => {
    if (!canvasRef.current || appRef.current) return;
    let cleanup: () => void = () => {};

    const canvas = canvasRef.current;

    (async function init() {
      const app = new Application();
      appRef.current = app;
      await app.init({
        resizeTo: canvasRef.current!,
        backgroundColor: 0xffffff,
        autoDensity: true,
        resolution: Math.min(1, window.devicePixelRatio || 1),
        antialias: false,
        backgroundAlpha: 1,
        powerPreference: "low-power",
        preserveDrawingBuffer: false,
        clearBeforeRender: true,
      });

      const rect = canvas.getBoundingClientRect();
      const viewport = new Viewport({
        screenWidth: rect.width,
        screenHeight: rect.height,
        worldWidth: rect.width,
        worldHeight: rect.height,
        events: app.renderer.events,
      });
      viewport.drag().pinch().wheel({ percent: 0.2, smooth: 2 }).decelerate({
        friction: 0.85,
      });
      // .clamp({ direction: "all" });

      viewportRef.current = viewport;

      // Particle mosaic container
      const particleContainer = new MosaicContainer(app, viewport);
      particleContainerRef.current = particleContainer;

      // after a replay finishes, enable replay button
      particleContainer.on("redrawComplete", () => {
        setReplayReady(true);
        setIsReplaying(false);
      });

      brushRef.current = new Brush(
        tileWidth * 2, // brush size
        app,
        viewport,
        particleContainer
      );

      app.stage.addChild(viewport);

      // Resize handler: resize to container div dimensions
      const resizeHandler = () => {
        if (!canvasRef.current || !viewportRef.current) return;
        const rect = canvasRef.current.getBoundingClientRect();
        setTimeout(() => {
          if (particleContainerRef.current) {
            fitToScreen(rect, viewport, particleContainerRef.current);
          }
        }, 3000); // delay to allow for any CSS transitions
      };

      window.addEventListener("resize", resizeHandler);

      // also resize on rotate device
      window.addEventListener("orientationchange", resizeHandler);

      // Mount canvas
      canvas.appendChild(app.canvas);
      canvas.style.touchAction = "none";

      cleanup = () => {
        window.removeEventListener("resize", resizeHandler);
        brushRef.current?.destroy();
        particleContainerRef.current?.destroy(true);
        appRef.current?.destroy(true, true);
        brushRef.current = null;
        particleContainerRef.current = null;
        appRef.current = null;
      };

      const loaded = await initInProgress(particleContainer);
      await wait(2000);

      // Update the palette and numbers
      if (loaded) {
        updateMosaic(canvas, viewport, particleContainer);
      } else {
        setIsLoading(false);
      }
    })();

    return () => cleanup();
    // make sure to only init once
  }, []);

  // Handle clicks on the canvas
  useEffect(() => {
    const app = appRef.current;
    const canvas = canvasRef.current;
    if (!app || !canvas) {
      return;
    }

    const toWorld = (clientX: number, clientY: number) => {
      const vp = viewportRef.current;
      const pc = particleContainerRef.current;
      if (!vp || !pc) return;

      // 1) Get screen coords in *device* pixels (not CSS px)
      const rect = canvas.getBoundingClientRect();
      const res = app.renderer.resolution ?? window.devicePixelRatio ?? 1;
      const sx = (clientX - rect.left) * res;
      const sy = (clientY - rect.top) * res;

      // 2) Convert screen -> world (viewport space)
      const world = vp.toWorld(sx, sy); // { x, y } in world units

      // 3) Convert world -> grid cell (account for container position/scale)
      // If you keep each cell as a 1x1 particle AND scale the whole container to tile size:
      // const gx = Math.floor((world.x - pc.x) / pc.scale.x);
      // const gy = Math.floor((world.y - pc.y) / pc.scale.y);

      // If instead your particles are already placed at x*tileWidth, y*tileWidth
      // and pc.scale == 1, use:
      const gx = Math.floor((world.x - pc.x) / tileWidth);
      const gy = Math.floor((world.y - pc.y) / tileWidth);

      // 4) Clamp & use
      if (gx < 0 || gy < 0 || gx >= pc.gridW || gy >= pc.gridH) return;

      return { x: gx, y: gy };
    };

    // log tile coordinates on click
    const clickHandler = (e: MouseEvent) => {
      const coords = toWorld(e.clientX, e.clientY);
      if (!coords) return;

      onClickTile(); // gx, gy);
    };

    const pointerupHandler = () => {
      if (isColouring) {
        setIsColouring(false);
        viewportRef.current?.plugins.resume("drag");
        // auto-advance to next unfinished color when current is complete
        const pc = particleContainerRef.current;
        if (pc && selectedColorIndex != null && viewportRef.current) {
          if (checkEnd()) {
            saveMosaic(pc);
            return;
          }

          const prog = pc.getProgress(selectedColorIndex) ?? 0;
          if (prog >= 1) {
            confetti(pc.getPalette()); // celebrate!

            const nextIdx = nextSelectedColour(selectedColorIndex, palette);
            if (nextIdx !== undefined) {
              selectColour(nextIdx);
              const el = paletteScrollRef.current?.querySelector(
                `[data-index="${selectedColorIndex}"]`
              ) as HTMLElement | null;
              el?.scrollIntoView({ behavior: "smooth", inline: "center" });
            }
          }
        }

        // schedule a save (debounced)
        saveDebounced();
      }
    };

    const pointerMoveHandler = (e: PointerEvent) => {
      if (!isColouring) return;

      const coords = toWorld(e.clientX, e.clientY);
      if (!coords || !isColouring) return;
      // const { x: gx, y: gy } = coords;
      onMoveTile(); // gx, gy);
    };

    const vc = viewportRef.current;
    if (!vc) {
      return;
    }

    vc.on("pointerdown", clickHandler);
    vc.on("pointerup", pointerupHandler);
    vc.on("pointercancel", pointerupHandler);
    vc.on("pointerout", pointerupHandler);
    vc.on("pointermove", pointerMoveHandler);

    return () => {
      vc.off("pointerdown", clickHandler);
      vc.off("pointerup", pointerupHandler);
      vc.off("pointercancel", pointerupHandler);
      vc.off("pointerout", pointerupHandler);
      vc.off("pointermove", pointerMoveHandler);
    };
  }, [
    isColouring,
    onClickTile,
    onMoveTile,
    selectedColorIndex,
    palette,
    saveDebounced,
  ]);

  // show/hide brush when colouring
  useEffect(() => {
    const brush = brushRef.current;
    if (!brush) return;

    if (isColouring) {
      brush.show(selectedColorIndex);
    } else {
      brush.hide();
    }
  }, [isColouring, selectedColorIndex]);

  const tpRaw = getTotalProgress(); // 0..1

  const selectedColourProgress =
    selectedColorIndex === undefined
      ? null
      : getPaletteProgress(selectedColorIndex);

  // saved works modal state
  const [savedWorks, setSavedWorks] = useState<SavedGalleryEntry[]>([]);
  const [showSaved, setShowSaved] = useState(false);

  const refreshSaved = async () => {
    const drawings = await Db.getInstance().getAllDrawings();
    const entries: SavedGalleryEntry[] = [];
    const entriesMap = new Map<string, SavedGalleryEntry>();
    for (const d of drawings) {
      const [blob, progress] = await toPng(d, 2, "alpha");
      const url = await new Promise<string>((res) => {
        const fr = new FileReader();
        fr.onload = () => res(fr.result as string);
        fr.readAsDataURL(blob);
      });
      const entry = { thumb: url, drawing: d, progress };
      entries.push(entry);
      entriesMap.set(d.id, entry);
    }
    setSavedWorks(entries);
    setGalleryList((list) =>
      list.map(
        (img): BuiltinGalleryEntry => ({
          ...img,
          saved: entriesMap.get(img.id),
        })
      )
    );
  };

  // load saved works right before showing the modal
  const openSaved = async () => {
    await refreshSaved();
    setShowSaved(true);
  };

  // Delete handler for saved gallery entries
  const deleteSaved = useCallback((entry: SavedGalleryEntry) => {
    if (!window.confirm("Are you sure you want to delete this saved drawing?"))
      return;
    deleteDrawing(entry.drawing.id);
    setSavedWorks((prev) =>
      prev.filter((e) => e.drawing.id !== entry.drawing.id)
    );
    setGalleryList((list) =>
      list.map(
        (img): BuiltinGalleryEntry => ({
          ...img,
          saved: img.id === entry.drawing.id ? undefined : img.saved,
        })
      )
    );
  }, []);

  // replay controls: hide button until first replay completes
  const [isReplaying, setIsReplaying] = useState(false);
  const [replayReady, setReplayReady] = useState(false);
  const onReplay = useCallback(() => {
    const pc = particleContainerRef.current;
    if (!pc || !viewportRef.current) return;
    setIsReplaying(true);
    pc.redraw();
  }, []);

  const hasStartedColouring = tpRaw > 0;

  return (
    <>
      <div
        style={{
          height: `calc(10px + var(--safe-top))`,
          boxSizing: "border-box",
          position: "relative",
          paddingTop: "var(--safe-top)", // pushes content below the status bar/notch
        }}
      >
        <div
          style={{
            position: "absolute",
            bottom: 0,
            width: "100%",
          }}
        >
          {selectedColor && selectedColourProgress !== null && (
            <ColorProgress
              color={selectedColor}
              progress={selectedColourProgress}
            />
          )}
        </div>
      </div>
      <div
        style={{
          width: "100vw",
          height: "calc(100dvh - (20px + var(--safe-top)))",
          position: "relative",
          overflow: "hidden",
          touchAction: "none",
          display: "flex",
          flexDirection: "row",
          background: "#111",
          color: "#fff",
          paddingBottom: "var(--safe-bottom)",
          boxSizing: "border-box",
        }}
      >
        {/* Left column: canvas, controls, and palette */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            flex: 1,
            minHeight: 0,
            minWidth: 0,
            paddingRight: "calc(var(--toolbar-w) + var(--safe-right))",
            boxSizing: "border-box",
          }}
        >
          {/* Main content area: canvas and toolbar */}
          {/* Canvas container (always mounted) */}
          <div
            ref={canvasRef}
            style={{
              flex: "1 1 auto",
              minHeight: 0, // KEY: allow shrinking
              minWidth: 0,
              position: "relative", // canvas will be absolutely positioned
              overflow: "hidden",
              touchAction: "none",
              boxSizing: "border-box",
              background: "#000",
            }}
          />

          {/* Loading overlay */}
          {isLoading && <PixelLoader />}

          {/* Overlay blank space */}
          {!isInit && !isLoading && (
            <div
              style={{
                position: "fixed",
                inset: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                pointerEvents: "auto", // allow clicks
                background: "#000", // match canvas background
                zIndex: 10,
              }}
            >
              <BlankSpace
                onStartDrawing={openGallery}
                onTakePicture={openCameraPicker}
                onOpenPhoto={openFolderPicker}
              />
            </div>
          )}

          {!hasStartedColouring && showDetailSelector && (
            <div
              style={{
                position: "absolute",
                top: "calc(20px + var(--safe-top, 0px))",
                left: "50%",
                transform: "translateX(-50%)",
                display: "flex",
                alignItems: "center",
                gap: 12,
                padding: "8px 10px",
                borderRadius: 999,
                color: "#fff",
                background: "rgba(0,0,0,0.45)",
                border: "1px solid rgba(255,255,255,0.18)",
                backdropFilter: "blur(6px)",
                WebkitBackdropFilter: "blur(6px)",
                boxShadow: "0 6px 16px rgba(0,0,0,0.35)",
                zIndex: 1000,
              }}
              role="radiogroup"
              aria-label="Detail level"
            >
              {[2, 3, 4].map((level) => {
                const selected = detail === level;
                return (
                  <button
                    key={level}
                    onClick={() => setDetailValue(level)}
                    role="radio"
                    aria-checked={selected}
                    title={`Detail ${level}`}
                    style={{
                      width: 36,
                      height: 36,
                      display: "grid",
                      placeItems: "center",
                      borderRadius: 8,
                      border: selected
                        ? "1px solid #000"
                        : "1px solid rgba(255,255,255,0.6)",
                      background: selected ? "#fff" : "rgba(255,255,255,0.06)",
                      color: selected ? "#000" : "#fff",
                      cursor: "pointer",
                      transition:
                        "transform 120ms ease, background 120ms ease, border-color 120ms ease",
                      outline: "none",
                    }}
                    onMouseDown={(e) =>
                      (e.currentTarget.style.transform = "scale(0.95)")
                    }
                    onMouseUp={(e) =>
                      (e.currentTarget.style.transform = "scale(1)")
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.transform = "scale(1)")
                    }
                  >
                    {level === 2 && (
                      <svg
                        width="22"
                        height="22"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                        aria-hidden="true"
                      >
                        {/* Single block */}
                        <rect x="4" y="4" width="16" height="16" rx="2" />
                      </svg>
                    )}
                    {level === 3 && (
                      <svg
                        width="22"
                        height="22"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                        aria-hidden="true"
                      >
                        {/* 2×2 grid with spacing */}
                        <rect x="4" y="4" width="7" height="7" rx="1" />
                        <rect x="13" y="4" width="7" height="7" rx="1" />
                        <rect x="4" y="13" width="7" height="7" rx="1" />
                        <rect x="13" y="13" width="7" height="7" rx="1" />
                      </svg>
                    )}
                    {level === 4 && (
                      <svg
                        width="22"
                        height="22"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                        aria-hidden="true"
                      >
                        {/* 3×3 grid */}
                        {Array.from({ length: 3 }).map((_, row) =>
                          Array.from({ length: 3 }).map((_, col) => (
                            <rect
                              key={`${row}-${col}`}
                              x={5 + col * 6}
                              y={5 + row * 6}
                              width="4"
                              height="4"
                              rx="1"
                            />
                          ))
                        )}
                      </svg>
                    )}
                  </button>
                );
              })}

              {/* Divider dot */}
              <div
                aria-hidden
                style={{
                  width: 6,
                  height: 6,
                  borderRadius: "50%",
                  background: "rgba(255,255,255,0.35)",
                  marginInline: 2,
                }}
              />

              {/* Icon-only confirm */}
              <button
                onClick={() => confirmDetail()}
                aria-label="Confirm detail"
                title="Confirm"
                style={{
                  width: 36,
                  height: 36,
                  display: "grid",
                  placeItems: "center",
                  borderRadius: 999,
                  border: "1px solid rgba(255,255,255,0.6)",
                  background: "rgba(255,255,255,0.08)",
                  color: "#fff",
                  cursor: "pointer",
                  transition:
                    "transform 120ms ease, background 120ms ease, border-color 120ms ease",
                  outline: "none",
                }}
                onMouseDown={(e) =>
                  (e.currentTarget.style.transform = "scale(0.95)")
                }
                onMouseUp={(e) =>
                  (e.currentTarget.style.transform = "scale(1)")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.transform = "scale(1)")
                }
              >
                {/* Check icon */}
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M20 6L9 17l-5-5" />
                </svg>
              </button>
            </div>
          )}

          {/* Palette bar */}
          <div
            style={{
              flex: "0 0 calc(var(--toolbar-button-size) + 30px)", // reserve height
              position: "relative",
              width: "100%",
              background: "rgba(0,0,0,0.3)",
              display: "flex",
              alignItems: "center",
              boxSizing: "border-box",
              paddingLeft: "var(--safe-left)",
            }}
          >
            {/* Scrollable swatches */}
            <div
              ref={paletteScrollRef} // attach ref here
              style={{
                overflowX: "auto",
                display: "flex",
                gap: 4,
                alignItems: "center",
                width: "100%",
                paddingBottom: 6, // leave room so the fixed bar doesn't overlap content
              }}
            >
              {palette.map((c, i) => {
                const [r8, g8, b8] = fromRgbHexValue(c);
                const lum = 0.299 * r8 + 0.587 * g8 + 0.114 * b8;
                const textColor = lum > 186 ? "#000" : "#fff";
                const key = c;
                const isSel = selectedColor === c;

                const pRaw = getPaletteProgress(i); // 0..1
                const p = Math.max(0, Math.min(1, pRaw || 0));

                return (
                  <div
                    key={key}
                    data-index={i} // mark each swatch
                    onClick={() => {
                      if (pRaw >= 1) {
                        // already done, do nothing
                        return;
                      }
                      selectColour(i);
                    }}
                    title={`Color #${i + 1} — ${Math.round(p * 100)}%`}
                    style={{
                      position: "relative",
                      flex: "0 0 auto",
                      width: "var(--toolbar-button-size)",
                      height: "var(--toolbar-button-size)",
                      borderRadius: 6,
                      cursor: "pointer",
                      border: isSel
                        ? "2px solid #fff"
                        : "2px solid transparent",
                      overflow: "hidden",
                      background: `#${c.toString(16).padStart(6, "0")}`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: textColor,
                      fontSize: 12,
                      fontWeight: 800,
                      userSelect: "none",
                    }}
                    aria-label={`Color ${i + 1}, ${Math.round(
                      p * 100
                    )} percent`}
                  >
                    <span style={{ lineHeight: 1 }}>
                      {p >= 1 ? "✓" : i + 1}
                    </span>

                    {/* Per-swatch progress bar */}
                    <div
                      style={{
                        position: "absolute",
                        left: 0,
                        right: 0,
                        bottom: 0,
                        height: 4,
                        background: "rgba(0,0,0,0.25)",
                      }}
                    />
                    <div
                      style={{
                        position: "absolute",
                        left: 0,
                        bottom: 0,
                        height: 4,
                        width: "100%", // fixed width; animate transform instead
                        background: "rgba(255,255,255,0.9)",
                        transform: `scaleX(${Math.max(0, Math.min(1, p))})`,
                        transformOrigin: "0 50%",
                        transition: "transform 160ms ease",
                        willChange: "transform",
                      }}
                    />
                  </div>
                );
              })}
            </div>

            {/* Fixed (non-scrolling) total progress bar */}
            {(() => {
              const tp = Math.max(0, Math.min(1, tpRaw || 0));

              return (
                // Track wrapper
                <div
                  role="progressbar"
                  aria-label={`Total progress ${Math.round(tp * 100)} percent`}
                  aria-valuenow={Math.round(tp * 100)}
                  aria-valuemin={0}
                  aria-valuemax={100}
                  style={{
                    position: "absolute",
                    left: 8,
                    right: 8,
                    bottom: 4,
                    height: 3,
                    background: "rgba(255,255,255,0.25)",
                    borderRadius: 2,
                    overflow: "hidden",
                    pointerEvents: "none",
                  }}
                >
                  {/* Fill (GPU-friendly) */}
                  <div
                    style={{
                      width: "100%",
                      height: "100%",
                      background: "rgba(255,255,255,0.9)",
                      transform: `scaleX(${tp})`,
                      transformOrigin: "0 50%",
                      transition: "transform 200ms ease",
                      willChange: "transform",
                    }}
                  />
                </div>
              );
            })()}
          </div>

          {/* Hidden file input for mobile capture */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            style={{ display: "none" }}
            onChange={onFileChange}
          />
          <input
            ref={fileCameraInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            style={{ display: "none" }}
            onChange={onFileChange}
          />
          <input
            ref={fileFolderInputRef}
            type="file"
            accept="image/*"
            style={{ display: "none" }}
            onChange={onFileChange}
          />
        </div>
        {/* close left column container */}

        {/* Right-side toolbar: full height */}
        <div
          style={{
            position: "absolute",
            top: 0,
            right: 0,
            paddingRight: "var(--safe-right)",
            bottom: "var(--safe-bottom)",
            width: "var(--toolbar-w)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            boxSizing: "content-box",
            background: "rgba(114,110,95,0.1)",
            borderLeft: "3px solid rgba(255,255,255,0.1)",
          }}
        >
          {/* Gallery button */}
          <button
            onClick={openGallery}
            title="Gallery"
            style={{
              width: "var(--toolbar-button-size)",
              height: "var(--toolbar-button-size)",
              padding: 0,
              border: "none",
              background: "transparent",
              cursor: "pointer",
              color: "#fff",
              marginTop: 8,
            }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              style={{
                width: "var(--toolbar-icon-size)",
                height: "var(--toolbar-icon-size)",
              }}
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-label="Open gallery"
            >
              <rect x="3" y="3" width="18" height="18" rx="2" />
              <circle cx="9" cy="9" r="1.4" />
              <path d="M7 17l3.2-3.6a1 1 0 0 1 1.4 0L14 17l3-4 3 4H7z" />
            </svg>
          </button>

          {/* Camera button */}
          <button
            onClick={openCameraPicker}
            title="Take Photo"
            style={{
              width: "var(--toolbar-button-size)",
              height: "var(--toolbar-button-size)",
              padding: 0,
              border: "none",
              background: "transparent",
              cursor: "pointer",
              color: "#fff",
              marginTop: 8,
            }}
          >
            <svg
              style={{
                width: "var(--toolbar-icon-size)",
                height: "var(--toolbar-icon-size)",
              }}
              viewBox="0 0 24 24"
              fill="currentColor"
            >
              <path d="M20 5h-3.17l-1.84-2H8.99L7.17 5H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 14H4V7h4.05l1.83-2h4.24l1.83 2H20v12z" />
              <circle cx="12" cy="13" r="3.5" />
            </svg>
          </button>

          {/* Folder button */}
          <button
            onClick={openFolderPicker}
            title="Choose from Files"
            style={{
              width: "var(--toolbar-button-size)",
              height: "var(--toolbar-button-size)",
              padding: 0,
              border: "none",
              background: "transparent",
              cursor: "pointer",
              color: "#fff",
              marginTop: 8,
            }}
          >
            <svg
              style={{
                width: "var(--toolbar-icon-size)",
                height: "var(--toolbar-icon-size)",
              }}
              viewBox="0 0 24 24"
              fill="currentColor"
            >
              <path d="M10 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2h-8l-2-2zM4 18V6h5.17l2 2H20v10H4z" />
            </svg>
          </button>

          {/* Saved works button */}
          <button
            onClick={openSaved}
            title="Saved Works"
            style={{
              width: "var(--toolbar-button-size)",
              height: "var(--toolbar-button-size)",
              padding: 0,
              background: "transparent",
              border: "none",
              cursor: "pointer",
              color: "#fff",
              marginTop: 8,
            }}
          >
            <svg
              style={{
                width: "var(--toolbar-icon-size)",
                height: "var(--toolbar-icon-size)",
              }}
              viewBox="0 0 24 24"
              fill="currentColor"
            >
              <path d="M3 4h18v4H3zm0 6h18v4H3zm0 6h18v4H3z" />
            </svg>
          </button>

          {/* Share button */}
          <button
            onClick={onShareRecording}
            title="Share"
            style={{
              width: "var(--toolbar-button-size)",
              height: "var(--toolbar-button-size)",
              padding: 0,
              border: "none",
              background: "transparent",
              cursor: "pointer",
              color: "#fff",
              marginTop: 8,
            }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M4 14v6h16v-6" />
              <path d="M12 4v9" />
              <path d="M6 10l6-6 6 6" />
            </svg>
          </button>

          {/* Restart button */}
          <button
            onClick={onRestart}
            title="Restart"
            style={{
              width: "var(--toolbar-button-size)",
              height: "var(--toolbar-button-size)",
              padding: 0,
              border: "none",
              background: "transparent",
              cursor: "pointer",
              color: "#fff",
              marginTop: 8,
            }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M1 4v6h6" />
              <path d="M23 20v-6h-6" />
              <path d="M20.49 9A9 9 0 0 0 5.21 5.5L1 10" />
              <path d="M3.51 15A9 9 0 0 0 18.79 18.5L23 14" />
            </svg>
          </button>

          {isDebug && (
            <SettingsSelect
              onExport={() => copyCurrentDrawing()}
              onImport={(text) => importDrawing(text)}
            />
          )}

          {/* Hint button */}
          <button
            onClick={() => {
              if (
                selectedColorIndex === undefined ||
                !viewportRef.current ||
                !particleContainerRef.current
              ) {
                return;
              }

              // perform pan hint
              const hinted = hintPan(
                selectedColorIndex,
                viewportRef.current,
                particleContainerRef.current
              );
              if (!hinted) return;

              // track and save hint usage
              particleContainerRef.current.incrementHintCount();
              saveMosaic(particleContainerRef.current);
            }}
            title="Hint Pan"
            style={{
              width: "var(--toolbar-button-size)",
              height: "var(--toolbar-button-size)",
              padding: 0,
              border: "none",
              background: "transparent",
              cursor: "pointer",
              color: "#fff",
              marginTop: "auto", // push hint to bottom
              marginBottom: 8,
            }}
          >
            {/* Lightbulb icon */}
            <svg
              style={{
                width: "var(--toolbar-icon-size)",
                height: "var(--toolbar-icon-size)",
              }}
              viewBox="0 0 24 24"
              fill="currentColor"
            >
              <path d="M12 2a7 7 0 0 0-7 7c0 2.9 1.6 5.1 3.6 6.4V18h6.8v-2.6C17.4 14.1 19 11.9 19 9a7 7 0 0 0-7-7zM8.5 20h7v1.5h-7V20z" />
            </svg>
          </button>
        </div>
      </div>

      {/* saved works modal */}
      <SavedGallery
        visible={showSaved}
        items={savedWorks}
        onSelect={(entry) => loadDrawing(entry.drawing)}
        onDelete={deleteSaved}
        onClose={() => setShowSaved(false)}
      />

      {/* gallery modal */}
      <BuiltinGallery
        visible={showGallery}
        items={galleryList}
        onSelect={loadBuiltin}
        onClose={closeGallery}
      />

      {showEnd && particleContainerRef.current && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            pointerEvents: "none",

            width: "calc(100vw - 48px)", // exclude toolbar
          }}
        >
          {/* Centered stars (unchanged) */}
          <div
            style={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <StarRatingOverlay
              value={particleContainerRef.current.stars}
              size={40}
              holdMs={3000}
              slideMs={750}
              delayMs={colouringFinished ? 4000 : 0}
              animate={colouringFinished}
            />
          </div>

          {/* Bottom-center replay button */}
          {replayReady && !isReplaying && (
            <div
              style={{
                position: "absolute",
                left: "50%",
                bottom: "calc(125px + var(--safe-bottom, 0px))",
                transform: "translateX(-50%)",
                pointerEvents: "auto",
              }}
            >
              <button
                onClick={onReplay}
                aria-label="Replay"
                title="Replay"
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: "linear-gradient(180deg, #fafafa, #e0e0e0)",
                  border: "1px solid rgba(255,255,255,0.3)",
                  borderRadius: 999,
                  cursor: "pointer",
                  opacity: 0.9,
                  transition: "opacity 150ms, transform 150ms, filter 150ms",
                  padding: 12,
                  filter: "drop-shadow(0 2px 4px rgba(0,0,0,.2))",
                  color: "#333",
                  backdropFilter: "blur(6px)",
                  WebkitBackdropFilter: "blur(6px)",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.opacity = "1";
                  e.currentTarget.style.transform = "scale(1.05)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.opacity = "0.9";
                  e.currentTarget.style.transform = "scale(1)";
                }}
              >
                {/* Play-in-circle icon */}
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="28"
                  height="28"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.75"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <circle cx="12" cy="12" r="8.5" />
                  <path
                    d="M10 8.75 16 12l-6 3.25Z"
                    fill="currentColor"
                    stroke="none"
                  />
                </svg>
              </button>
            </div>
          )}
        </div>
      )}
    </>
  );
}

export default App;
